import { Button, Dialog } from '@mui/material';
import { colorSet } from '@theme';
import { ISequenceCutListItem } from '@types';
import { useFetchImageDetail, useFetchSequenceCutList } from '@api';
import { useEffect, useRef, useState } from 'react';
import { Text, SecureImage } from '..';
import {
  FemaleBodyChildOptionValueEnum,
  SexualPositionChildOptionValueEnum,
  StandardShotChildOptionValueEnum,
  HorizontalAngleChildOptionValueEnum,
  VerticalAngleChildOptionValueEnum,
  AgeRangeChildOptionValueEnum,
  MainMotionPartChildOptionValueEnum,
  PostureCategoryChildOptionValueEnum,
  StandardFacialExpressionChildOptionValueEnum,
  SexualBodyPartChildOptionValueEnum,
  QueryParamKeyEnum
} from '@constant';
import { getEnumKeyByValue } from '@utils';
import { useLocation, useNavigate } from 'react-router';
import {
  ArrowRestoreWhiteIcon,
  ChevronLeftGray300Icon,
  ChevronLeftGray500Icon,
  ChevronRightGray300Icon,
  ChevronRightGray500Icon,
  TrashCanWhiteIcon,
  XBlackIcon
} from '@assets/iconComponents';

interface IDeletedImageModal {
  onClose: () => void;
  onRestoreButtonClick: () => void;
  onHardDeleteButtonClick: () => void;
  isPending: boolean;
}

export const DeletedImageModal = (props: IDeletedImageModal) => {
  const { onClose, onRestoreButtonClick, onHardDeleteButtonClick, isPending } = props;

  //시퀀스 컷 관련
  const [sequenceCutList, setSequenceCutList] = useState<ISequenceCutListItem[]>([]);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search); // location.search는 '?key=value' 형태
  const isImageModalVisible = queryParams.get(QueryParamKeyEnum.IS_IMAGE_MODAL_VISIBLE) === 'true';
  const initialImageIdQueryParamValue = queryParams.get(QueryParamKeyEnum.INITIAL_IMAGE_ID)
    ? Number(queryParams.get(QueryParamKeyEnum.INITIAL_IMAGE_ID))
    : null;
  const imageIdQueryParamValue = queryParams.get(QueryParamKeyEnum.IMAGE_ID)
    ? Number(queryParams.get(QueryParamKeyEnum.IMAGE_ID))
    : null;
  const sequenceIdQueryParamValue = queryParams.get(QueryParamKeyEnum.SEQUENCE_ID)
    ? Number(queryParams.get(QueryParamKeyEnum.SEQUENCE_ID))
    : null;

  const needSequence = Boolean(imageIdQueryParamValue && sequenceIdQueryParamValue); //시퀀스컷

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const visibleCount = 6; // 화면에 완전히 보여지는 시퀀스 컷 리스트 아이템 갯수
  const partialVisibleCount = 7; // 잘려보이는 마지막 요소까지 포함한 시퀀스 컷 리스트 아이템 갯수

  const navigate = useNavigate();
  const locationPathname = location.pathname;

  const { data: imageDetailData, isLoading: isImageDetailLoading } = useFetchImageDetail(
    isImageModalVisible && imageIdQueryParamValue ? imageIdQueryParamValue : undefined
  );

  const { data: sequenceCutListData } = useFetchSequenceCutList(
    isImageModalVisible && sequenceIdQueryParamValue ? sequenceIdQueryParamValue : undefined,
    '&isIncludeDeleted=true' // 삭제된 이미지를 포함한 시퀀스 컷 리스트를 응답하도록 함
  );

  // 모달 타이틀 텍스트값 결정 및 각각의 필터값 렌더링 여부 결정
  const isStandardImage = Boolean(imageDetailData?.imageOptionValue['배경컷'] !== undefined);
  const isAdultsOnlyImage = Boolean(imageDetailData?.imageOptionValue['체위']);

  const handleSequenceCutListItemClick = (imageId: number) => {
    queryParams.set(QueryParamKeyEnum.IMAGE_ID, imageId.toString());

    navigate(`${locationPathname}?${queryParams.toString()}`);
  };

  const handleSequenceCutListPrevButtonClick = () => {
    if (visibleStartIndex > 0) {
      setVisibleStartIndex((prevIndex) => (prevIndex - visibleCount > 0 ? prevIndex - visibleCount : 0));
    }
  };

  const handleSequenceCutListNextButtonClick = () => {
    if (visibleStartIndex + visibleCount < sequenceCutList.length) {
      setVisibleStartIndex((prevIndex) => prevIndex + visibleCount);
    }
  };

  //시퀀스 이미지 리스트에서 선택된 컷을 찾아 isFocused: true 처리
  useEffect(() => {
    if (isImageModalVisible && imageDetailData && sequenceCutListData && sequenceCutListData.length > 0) {
      // imageDetailData.image.id와 같은 id를 가진 요소의 인덱스 찾기
      const selectedImageIndex = sequenceCutListData.findIndex((item) => item.id === imageDetailData?.image?.id);

      //imageList state에 isFocused 프로퍼티 추가
      const updatedImageList = sequenceCutListData.map((item, index) => ({
        ...item, // 기존의 속성들을 그대로 복사
        isFocused: index === selectedImageIndex // 찾은 인덱스와 일치하는 경우에만 isFocused를 true로 설정
      }));
      setSequenceCutList([...updatedImageList]);

      //visibleCount보다 이후 이미지라면 페이지 넘김 처리
      const pageIndex = Math.floor(selectedImageIndex / visibleCount) * visibleCount;
      setVisibleStartIndex(pageIndex);
    } else {
      setSequenceCutList([]);
    }
  }, [isImageModalVisible, sequenceCutListData, imageDetailData]);

  return (
    <>
      <Dialog
        open={isImageModalVisible}
        onClose={onClose}
        scroll="body"
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          },
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: 'calc(100% - 288px)',
            minHeight: 'calc(100% - 80px)',
            height: 'auto',
            maxHeight: 'auto',
            margin: '40px 144px',
            padding: '60px 40px',
            boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.06)',
            borderRadius: 0
          },
          '& .MuiDialog-container': {
            // 스크롤은 가능하되 스크롤바는 숨김
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
            '&::-webkit-scrollbar': {
              display: 'none' // Chrome, Safari
            }
          }
        }}
      >
        <div ref={scrollRef}>
          <XBlackIcon
            style={{ width: 24, height: 24, position: 'absolute', top: 24, right: 24, cursor: 'pointer' }}
            onClick={onClose}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', gap: 60 }}>
          {/* 좌측: 선택된 컷 */}
          <div>
            {isImageDetailLoading ? <div style={{ width: 540, height: 540 }} /> : null}
            <div
              style={{
                width: 540,
                height: 'auto',
                borderRadius: '6px',
                border: `1px solid ${colorSet.gray300}`,
                backgroundColor: colorSet.gray100
              }}
            >
              <SecureImage
                imageUrl={imageDetailData?.image.s3Url ?? ''}
                imageStyle={{ width: 538, height: 'auto', objectFit: 'contain', borderRadius: '6px' }}
              />
            </div>
          </div>
          {/* 좌측 end */}

          {/* 우측: 작품명 / 필터값 / 관련태그 / 시퀀스컷 */}
          <div style={{ width: '100%' }}>
            <div
              style={{
                padding: 20,
                border: `1px solid ${colorSet.gray200}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '32px'
              }}
            >
              {/* 작품명 */}
              {isImageDetailLoading ? <div style={{ height: 28 }} /> : null}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10
                  }}
                >
                  <Text variant="h3" color={colorSet.gray500} fontWeight="bold">
                    {isStandardImage ? '[스탠다드 검색]' : '[19금]'} {imageDetailData?.comic?.name ?? ''}
                  </Text>
                </div>
              </div>

              {/* 필터값 */}
              {isImageDetailLoading ? <div style={{ height: 74 }} /> : null}
              {imageDetailData && imageDetailData.imageOptionValue ? (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  {imageDetailData?.comic?.genre ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        장르: {imageDetailData?.comic?.genre}
                      </Text>
                    </div>
                  ) : null}

                  {isStandardImage && imageDetailData?.imageOptionValue['배경컷'] ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        배경컷
                      </Text>
                    </div>
                  ) : null}

                  {isAdultsOnlyImage && imageDetailData?.imageOptionValue['체위'] ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        체위:{' '}
                        {(() => {
                          const sexualPositionValue = imageDetailData?.imageOptionValue['체위'];
                          return getEnumKeyByValue(SexualPositionChildOptionValueEnum, sexualPositionValue);
                        })()}
                      </Text>
                    </div>
                  ) : null}

                  {isAdultsOnlyImage && imageDetailData?.imageOptionValue['여체'] ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        여체:{' '}
                        {(() => {
                          const femaleBodyValue = imageDetailData?.imageOptionValue['여체'];
                          return getEnumKeyByValue(FemaleBodyChildOptionValueEnum, femaleBodyValue);
                        })()}
                      </Text>
                    </div>
                  ) : null}

                  {isAdultsOnlyImage &&
                  imageDetailData?.imageOptionValue['신체부위'] &&
                  imageDetailData?.imageOptionValue['신체부위'].length > 0 ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        신체부위:{' '}
                        {(() => {
                          const bodyPartValue = imageDetailData?.imageOptionValue['신체부위'];
                          return getEnumKeyByValue(SexualBodyPartChildOptionValueEnum, bodyPartValue) ?? '';
                        })()}
                      </Text>
                    </div>
                  ) : null}

                  {imageDetailData?.imageOptionValue['남성 인원수'] ||
                  imageDetailData?.imageOptionValue['여성 인원수'] ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        인원수:{' '}
                        {imageDetailData?.imageOptionValue['남성 인원수'] &&
                        ((typeof imageDetailData?.imageOptionValue['남성 인원수'] === 'number' &&
                          imageDetailData?.imageOptionValue['남성 인원수'] > 0) ||
                          imageDetailData?.imageOptionValue['남성 인원수'] === '4+')
                          ? `남자 ${imageDetailData?.imageOptionValue['남성 인원수']}명`
                          : ''}{' '}
                        {imageDetailData?.imageOptionValue['여성 인원수'] &&
                        ((typeof imageDetailData?.imageOptionValue['여성 인원수'] === 'number' &&
                          imageDetailData?.imageOptionValue['여성 인원수'] > 0) ||
                          imageDetailData?.imageOptionValue['여성 인원수'] === '4+')
                          ? `여자 ${imageDetailData?.imageOptionValue['여성 인원수']}명`
                          : ''}
                      </Text>
                    </div>
                  ) : null}

                  {imageDetailData?.imageOptionValue['샷 구분'] ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        프레임:{' '}
                        {(() => {
                          const shotValue = imageDetailData?.imageOptionValue['샷 구분'];
                          return getEnumKeyByValue(StandardShotChildOptionValueEnum, shotValue);
                        })()}
                      </Text>
                    </div>
                  ) : null}

                  {isStandardImage && imageDetailData?.imageOptionValue['연출'] ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        연출
                      </Text>
                    </div>
                  ) : null}

                  {isStandardImage && imageDetailData?.imageOptionValue['세로앵글'] ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        세로앵글:{' '}
                        {(() => {
                          const verticalAngleValue = imageDetailData?.imageOptionValue['세로앵글'];
                          return getEnumKeyByValue(VerticalAngleChildOptionValueEnum, verticalAngleValue);
                        })()}
                      </Text>
                    </div>
                  ) : null}

                  {isStandardImage &&
                  imageDetailData?.imageOptionValue['연령대'] &&
                  imageDetailData?.imageOptionValue['연령대'].length > 0 ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        연령대:{' '}
                        {(() => {
                          const ageRangeValue = imageDetailData?.imageOptionValue['연령대'];
                          return getEnumKeyByValue(AgeRangeChildOptionValueEnum, ageRangeValue);
                        })()}
                      </Text>
                    </div>
                  ) : null}

                  {isStandardImage &&
                  imageDetailData?.imageOptionValue['중심동작부위'] &&
                  imageDetailData?.imageOptionValue['중심동작부위'].length > 0 ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        중심 동작 부위:{' '}
                        {(() => {
                          const mainMotionPartValue = imageDetailData?.imageOptionValue['중심동작부위'];
                          return getEnumKeyByValue(MainMotionPartChildOptionValueEnum, mainMotionPartValue);
                        })()}
                      </Text>
                    </div>
                  ) : null}

                  {isStandardImage &&
                  imageDetailData?.imageOptionValue['표정'] &&
                  imageDetailData?.imageOptionValue['표정'].length > 0 ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        표정:{' '}
                        {(() => {
                          const facialExpressionValue = imageDetailData?.imageOptionValue['표정'];
                          return (
                            getEnumKeyByValue(StandardFacialExpressionChildOptionValueEnum, facialExpressionValue) ?? ''
                          );
                        })()}
                      </Text>
                    </div>
                  ) : null}

                  {isStandardImage && imageDetailData?.imageOptionValue['데포르메 여부'] ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        데포르메
                      </Text>
                    </div>
                  ) : null}

                  {imageDetailData?.imageOptionValue['가로앵글'] &&
                  imageDetailData?.imageOptionValue['가로앵글'].length > 0 ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        가로앵글:{' '}
                        {(() => {
                          const horizontalAngleValue = imageDetailData?.imageOptionValue['가로앵글'];
                          return (
                            getEnumKeyByValue(HorizontalAngleChildOptionValueEnum, horizontalAngleValue) ??
                            horizontalAngleValue
                          );
                        })()}
                      </Text>
                    </div>
                  ) : null}

                  {isStandardImage &&
                  imageDetailData?.imageOptionValue['동작구분'] &&
                  imageDetailData?.imageOptionValue['동작구분'].length > 0 ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        동작구분:{' '}
                        {(() => {
                          const postureCategoryValue = imageDetailData?.imageOptionValue['동작구분'];
                          return (
                            getEnumKeyByValue(PostureCategoryChildOptionValueEnum, postureCategoryValue) ??
                            postureCategoryValue
                          );
                        })()}
                      </Text>{' '}
                    </div>
                  ) : null}

                  {isStandardImage && imageDetailData?.imageOptionValue['스킨십'] ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        스킨십
                      </Text>
                    </div>
                  ) : null}

                  {isAdultsOnlyImage && imageDetailData?.imageOptionValue['세로앵글'] ? (
                    <div
                      style={{
                        height: 32,
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 12px'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        세로앵글:{' '}
                        {(() => {
                          const verticalAngleValue = imageDetailData?.imageOptionValue['세로앵글'];
                          return (
                            getEnumKeyByValue(VerticalAngleChildOptionValueEnum, verticalAngleValue) ??
                            verticalAngleValue
                          );
                        })()}
                      </Text>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* 관련 태그 */}
              <div>
                <Text variant="subtitle" fontWeight="bold" marginBottom="10px">
                  관련 태그
                </Text>

                {imageDetailData && imageDetailData.metaTag && imageDetailData.metaTag.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: `10px`,
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    {imageDetailData.metaTag.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: '6px',
                          backgroundColor: colorSet.gray200,
                          padding: '8px 20px'
                        }}
                      >
                        <Text variant="body1">{item.name}</Text>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    {isImageDetailLoading ? (
                      <div style={{ height: 20 }} />
                    ) : (
                      <div style={{ padding: '6px 10px' }}>
                        <Text variant="body1" color={colorSet.gray500}>
                          관련 태그가 없습니다.
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 시퀀스 컷 리스트 */}
              {needSequence && sequenceCutList && sequenceCutList.length > 0 ? (
                <div>
                  <Text variant="subtitle" fontWeight="bold" marginBottom="10px">
                    기존 시퀀스 컷
                  </Text>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 4px',
                      border: `1px solid ${colorSet.gray300}`,
                      borderRadius: '6px',
                      gap: 4,
                      marginBottom: '10px'
                    }}
                  >
                    {visibleStartIndex === 0 ? (
                      <ChevronLeftGray300Icon />
                    ) : (
                      <ChevronLeftGray500Icon
                        onClick={handleSequenceCutListPrevButtonClick}
                        style={{ cursor: 'pointer' }}
                      />
                    )}

                    <div
                      style={{
                        width: '100%',
                        overflow: 'hidden' //마지막 요소의 부모 영역을 벗어난 부분이 보이지 않도록
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `repeat(${partialVisibleCount}, 1fr)`,
                          gap: 4,
                          width: 'calc(100% + 60px)' //마지막 요소가 60px만 보이고 부모 영역을 벗어나 가려지도록 처리
                        }}
                      >
                        {sequenceCutList
                          .slice(visibleStartIndex, visibleStartIndex + partialVisibleCount)
                          .map((item, index) => (
                            <div
                              key={item.id}
                              onClick={() => {
                                handleSequenceCutListItemClick(item.id);
                                if (index === visibleCount) {
                                  handleSequenceCutListNextButtonClick();
                                }
                              }}
                              style={{
                                position: 'relative',
                                width: '100%',
                                aspectRatio: 1,
                                cursor: 'pointer'
                              }}
                            >
                              <SecureImage
                                imageUrl={item.s3Url}
                                imageStyle={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '2px',
                                  border: item.isFocused
                                    ? `4px solid ${colorSet.primaryMain}`
                                    : `1px solid ${colorSet.gray300}`
                                }}
                              />
                              {item.isFocused ? (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(115, 103, 240, 0.4)',
                                    borderRadius: '6px' // 부모 div와 동일한 border-radius 적용
                                  }}
                                />
                              ) : null}
                            </div>
                          ))}
                      </div>
                    </div>

                    {visibleStartIndex + visibleCount >= sequenceCutList.length ? (
                      <ChevronRightGray300Icon />
                    ) : (
                      <ChevronRightGray500Icon
                        onClick={handleSequenceCutListNextButtonClick}
                        style={{ cursor: 'pointer' }}
                      />
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            {initialImageIdQueryParamValue &&
              imageIdQueryParamValue &&
              initialImageIdQueryParamValue === imageIdQueryParamValue && (
                <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', marginTop: '24px' }}>
                  <Button
                    disabled={isPending}
                    onClick={onRestoreButtonClick}
                    variant="contained"
                    color="primary"
                    style={{
                      width: 120,
                      height: 44,
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 10,
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <ArrowRestoreWhiteIcon />
                    복구
                  </Button>
                  <Button
                    disabled={isPending}
                    onClick={onHardDeleteButtonClick}
                    variant="contained"
                    color="primary"
                    style={{
                      width: 145,
                      height: 44,
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 10,
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <TrashCanWhiteIcon />
                    영구삭제
                  </Button>
                </div>
              )}
          </div>
          {/* 우측 end */}
        </div>
      </Dialog>
    </>
  );
};
